// Requirement: `#6951` - on a job shared with the current zone, its managers and admins may tick
// the Crew Pay assignment boxes (Table 1 of the crew-pay table) and nothing else. Those ticks
// are saved through setCrewPayAssignments instead of the full job save, which belongs to the
// job's owner. Everything that tells the two saves apart lives here.

import { CrewPayAssignmentInput, CrewPayPhase } from 'src/generated/graphql.generated';

import { quickJsActions } from '../../../../quickjs/quickjs.actions';
import type { UnsavedChange } from '../../../../state/unsaved-changes.reducer';

import { CrewPayColumnKey } from './crew-pay.constants';

// Table 1's columns (every crew-pay column but the bonus, which lives in Table 2), as they appear
// in `crewPay__<userId>__<phase>__enabled`.
const CREW_PAY_PHASE_BY_KEY: Record<Exclude<CrewPayColumnKey, 'bonus'>, CrewPayPhase> = {
    crewLead: CrewPayPhase.CrewLead,
    flatRate: CrewPayPhase.FlatRate,
    packing: CrewPayPhase.Packing,
    unpacking: CrewPayPhase.Unpacking,
    load: CrewPayPhase.Load,
    unload: CrewPayPhase.Unload,
};

const CREW_PAY_ASSIGNMENT_KEY = new RegExp(
    `^crewPay__(.+)__(${ Object.keys(CREW_PAY_PHASE_BY_KEY).join('|') })__enabled$`,
);

/** The crew member and phase a calculator input key assigns, or undefined for any other key. */
export function parseCrewPayAssignmentKey(key: string): Pick<CrewPayAssignmentInput, 'crewMemberId' | 'phase'> | undefined {
    const match = CREW_PAY_ASSIGNMENT_KEY.exec(key);
    if (!match) { return undefined; }

    return { crewMemberId: match[1], phase: CREW_PAY_PHASE_BY_KEY[match[2] as keyof typeof CREW_PAY_PHASE_BY_KEY] };
}

/** The calculator input key an unsaved change sets, when it is a Crew Pay assignment box. */
export function crewPayAssignmentChangeKey(change: UnsavedChange): string | undefined {
    if (change.action.type !== quickJsActions.inputChanged.type) { return undefined; }

    const { key } = change.action as ReturnType<typeof quickJsActions.inputChanged>;

    return parseCrewPayAssignmentKey(key) ? key : undefined;
}
